import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

type LandingAtmosphereProps = {
  className?: string;
};

/**
 * Full-bleed atmospheric plane for the public login / marketing landing.
 * Sky + soft foliage wash + silhouette flock — not abstract purple orbs.
 */
export default function LandingAtmosphere({ className = '' }: LandingAtmosphereProps) {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_10%_0%,#d8f3ef_0%,transparent_55%),radial-gradient(90%_70%_at_90%_10%,#c7e7f5_0%,transparent_50%),linear-gradient(165deg,#e8f6f3_0%,#eef4f8_42%,#f4f7f2_100%)]" />

      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg stroke=\'%230d9488\' stroke-opacity=\'0.12\' stroke-width=\'1\'%3E%3Cpath d=\'M0 30h60M30 0v60\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }}
      />

      <svg
        className={`absolute -left-16 top-[-8%] h-[52vh] w-[52vh] max-w-none opacity-70 ${
          reducedMotion ? '' : 'animate-[float_18s_ease-in-out_infinite]'
        }`}
        viewBox="0 0 600 600"
      >
        <defs>
          <radialGradient id="landing-orb-teal" cx="45%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#5eead4" stopOpacity="0.85" />
            <stop offset="55%" stopColor="#0d9488" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="300" cy="300" r="300" fill="url(#landing-orb-teal)" />
      </svg>

      <svg
        className={`absolute -right-24 top-[8%] h-[44vh] w-[44vh] max-w-none opacity-55 ${
          reducedMotion ? '' : 'animate-[float_22s_ease-in-out_infinite_reverse]'
        }`}
        viewBox="0 0 520 520"
      >
        <defs>
          <radialGradient id="landing-orb-sky" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.75" />
            <stop offset="60%" stopColor="#0284c7" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="260" fill="url(#landing-orb-sky)" />
      </svg>

      {/* Soft ground / foliage wash along the bottom edge */}
      <div className="absolute inset-x-0 bottom-0 h-[38%] bg-gradient-to-t from-emerald-900/15 via-teal-700/5 to-transparent" />

      {/* Dominant silhouette flock — edge-to-edge visual plane */}
      <svg
        className="absolute inset-x-0 bottom-0 h-[min(42vh,28rem)] w-full opacity-[0.22]"
        viewBox="0 0 1440 320"
        preserveAspectRatio="xMidYMax slice"
      >
        <path
          fill="#0f766e"
          d="M0,224 L80,210 C160,196 240,168 320,176 C400,184 480,228 560,236 C640,244 720,216 800,200 C880,184 960,180 1040,196 C1120,212 1200,248 1280,252 C1360,256 1400,224 1420,208 L1440,192 L1440,320 L0,320 Z"
        />
        <g fill="#115e59" opacity="0.9">
          <path d="M180 210 C195 185 225 170 255 178 C245 195 230 208 208 216 C222 218 238 224 250 236 C230 242 200 240 180 228 Z" />
          <path d="M420 198 C438 172 472 158 505 168 C492 186 474 200 450 208 C466 212 484 220 498 234 C474 240 440 236 420 220 Z" />
          <path d="M780 188 C802 158 842 142 880 156 C864 176 842 192 812 200 C832 206 854 216 870 232 C842 238 800 232 780 212 Z" />
          <path d="M1080 200 C1100 174 1134 160 1168 170 C1154 188 1134 202 1110 210 C1128 214 1148 222 1162 236 C1136 242 1100 238 1080 220 Z" />
          <path d="M1280 214 C1298 190 1328 178 1358 186 C1346 202 1330 214 1310 220 C1324 224 1340 230 1352 242 C1330 248 1298 244 1280 230 Z" />
        </g>
      </svg>

      {/* Mid-air silhouettes */}
      <svg
        className={`absolute left-[8%] top-[22%] h-14 w-20 opacity-40 ${
          reducedMotion ? '' : 'marketing-drift'
        }`}
        viewBox="0 0 120 90"
      >
        <path
          d="M12 62 C20 45, 42 30, 62 36 C56 46, 47 54, 34 59 C44 58, 53 60, 60 66 C48 70, 31 71, 18 66 Z"
          fill="#0f766e"
        />
      </svg>
      <svg
        className={`absolute right-[14%] top-[30%] h-12 w-16 opacity-35 ${
          reducedMotion ? '' : 'marketing-drift-slow'
        }`}
        viewBox="0 0 100 80"
      >
        <path
          d="M14 53 C22 39, 39 29, 55 34 C50 43, 42 50, 31 54 C39 55, 47 58, 53 64 C43 67, 29 67, 18 62 Z"
          fill="#0369a1"
        />
      </svg>
      <svg
        className={`absolute left-[42%] top-[16%] h-10 w-14 opacity-30 ${
          reducedMotion ? '' : 'marketing-drift-alt'
        }`}
        viewBox="0 0 100 80"
      >
        <path
          d="M14 53 C22 39, 39 29, 55 34 C50 43, 42 50, 31 54 C39 55, 47 58, 53 64 C43 67, 29 67, 18 62 Z"
          fill="#0d9488"
        />
      </svg>
    </div>
  );
}
